import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import CustomScrollTrigger from 'js/wrapper/CustomScrollTrigger';
import { Parallax } from 'react-scroll-parallax';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { getLocationsPath } from 'helpers/intl';
import { useAbstractCmsElement } from 'components/cms/hooks/useAbstractCmsElementNew';

import LocationsMap from 'components/layout/maps/LocationsMap';
import List from 'components/List';

const CmsElementLocations = (props) => {
  const { editMode, index, showElementSettings } = props;

  // CMS Logic via Hook
  const {
    shown,
    setShown,
    getMainId,
    getCustomId,
    getParamValue,
    getBackgroundStyle,
    elementStyleTag,
    getParallaxData,
    getAppendHtml,
    cssClasses,
    responsiveClasses
  } = useAbstractCmsElement(props);

  // Redux state
  const locations = useSelector((state) => state.list.companyLocations);

  // Local state for map view
  const [mapState, setMapState] = useState({
    defaultsInit: false,
    zoom: 13,
    center: { lat: 49.2401572, lng: 6.9969327 },
  });

  // dnd-kit Sortable Hook
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: index,
    disabled: !editMode
  });

  /**
   * Calculate zoom level based on bounds
   * Logic ported from the original inner class
   */
  const calculateZoomLevel = (west, east) => {
    const GLOBE_WIDTH = 256;
    let angle = east - west;
    if (angle < 0) angle += 360;

    let zoom = Math.round(Math.log((window.innerWidth * 360) / angle / GLOBE_WIDTH) / Math.LN2) - 1;
    return Math.min(zoom, 17);
  };

  /**
   * Calculate center and zoom based on the list of locations
   */
  useEffect(() => {
    if (locations?.data?.list && locations.data.list.length > 0 && !locations.pending) {
      const list = locations.data.list;
      let maxLat = list[0].coordinates.lat;
      let minLat = list[0].coordinates.lat;
      let maxLng = list[0].coordinates.lng;
      let minLng = list[0].coordinates.lng;

      list.forEach((element) => {
        if (element.coordinates.lat > maxLat) maxLat = element.coordinates.lat;
        if (element.coordinates.lat < minLat) minLat = element.coordinates.lat;
        if (element.coordinates.lng > maxLng) maxLng = element.coordinates.lng;
        if (element.coordinates.lng < minLng) minLng = element.coordinates.lng;
      });

      const centerLat = (maxLat + minLat) / 2.0;
      const centerLng = (maxLng + minLng) / 2.0;
      const zoom = calculateZoomLevel(minLng, maxLng);

      setMapState({
        center: { lat: centerLat, lng: centerLng },
        zoom,
        defaultsInit: true,
      });
    }
  }, [locations?.data?.list, locations?.pending]);

  // Helper for generating location detail links
  const getLink = (location) => {
    return `${getLocationsPath()}/${location.id}-${location.name}`;
  };

  // dnd-kit Styles
  const dndStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    position: 'relative',
    width: '100%',
    cursor: editMode ? 'move' : 'default'
  };

  const mapHeight = getParamValue('mapHeight', 300);
  const parallaxData = getParallaxData();
  const urlData = useMemo(() => ({ distance: 100 }), []);

  const innerContent = (
    <div
      id={getMainId()}
      className={`ms-locations ${shown ? 'elem-shown' : 'elem-hidden'} ${cssClasses} ${responsiveClasses}`}
      onClick={(e) => editMode && showElementSettings(e, index)}
      style={getBackgroundStyle()}
    >
      {elementStyleTag}

      {/* Data Fetcher Component */}
      <List
        noRender
        listName="companyLocations"
        url="/api/whitelabellocations.json"
        urlData={urlData}
        list={locations}
      />

      <div style={{ height: `${mapHeight}px` }} className="gmap m-t-10 p-b-30 sm-p-b-10">
        {!locations?.pending && mapState.defaultsInit && (
          <LocationsMap
            locations={locations}
            zoom={mapState.zoom}
            center={mapState.center}
            getLink={getLink}
          />
        )}
      </div>

      {getAppendHtml()}
    </div>
  );

  return (
    <div
      ref={setNodeRef}
      style={dndStyle}
      {...attributes}
      {...listeners}
    >
      <div>
        <CustomScrollTrigger className="cms-overflow-fix" onEnter={() => setShown(true)} {...getCustomId()}>
          {parallaxData ? (
            <Parallax {...parallaxData}>
              {innerContent}
            </Parallax>
          ) : (
            innerContent
          )}
        </CustomScrollTrigger>
      </div>
    </div>
  );
};

export default CmsElementLocations;
